import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { WorkOfArtService } from '../../../core/work-of-art/work-of-art.service';
import { filter, Subject, take, takeUntil } from 'rxjs';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { Router } from '@angular/router';
import { ArtistService } from '../../../core/artist/artist.service';
import Swiper from 'swiper/bundle';
import { MatDialogModule } from '@angular/material/dialog';
import { MatMenuModule } from '@angular/material/menu';
import { MatBottomSheetModule } from '@angular/material/bottom-sheet';
import { WorkofartCardComponent } from '../../components/cards/workofart-card/workofart-card.component';
import { MatSnackBar } from '@angular/material/snack-bar';
import { HttpErrorResponse } from '@angular/common/http';
import { urlExterna } from '../../../core/urls/external-url';

@Component({
  selector: 'app-work-of-art-modal',
  imports: [
    CommonModule,
    MatIconModule,
    MatDialogModule,
    MatMenuModule,
    WorkofartCardComponent,
  ],
  templateUrl: './work-of-art-modal.component.html',
  styleUrl: './work-of-art-modal.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WorkOfArtModalComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  datax = inject(DIALOG_DATA);
  dialogRef = inject(DialogRef);

  private destroy$ = new Subject<void>();

  loading = false;

  data: any = null;

  loadedImages = new Set<string>();

  constructor(
    private _workOfArtService: WorkOfArtService,
    private _cdr: ChangeDetectorRef,
    private router: Router,
    private _artistService: ArtistService,
    private _snackBar: MatSnackBar
  ) {}
  // Aquí puedes definir las propiedades y métodos necesarios para el modal
  // Por ejemplo, puedes tener una propiedad para almacenar los detalles de la obra de arte

  ngOnInit(): void {
    // C4: reset the subject to null before the call so stale/null
    // emission from the BehaviorSubject doesn't render wrong data.
    this._workOfArtService.workOfArt = null;

    this.loading = true;

    this._workOfArtService
      .getDetailsWorkOfArt(this.datax._id)
      .pipe(take(1))
      .subscribe({
        next: (workOfArt) => {
          this.loading = false;
          this.initializeSwiper();

          this._cdr.markForCheck();
        },
        error: (error) => {
          console.error(
            'Error al obtener los detalles de la obra de arte:',
            error
          );
          this.loading = false;

          // C3: on HTTP 404, close the modal and show a snackbar.
          if (error instanceof HttpErrorResponse && error.status === 404) {
            this.dialogRef.close();
            this._snackBar.open('Esta obra ya no está disponible.', 'Cerrar', {
              duration: 4000,
            });
          }

          this._cdr.markForCheck();
        },
      });

    // C4: filter out null/stale emissions so the template only
    // receives real data from the HTTP call.
    this._workOfArtService.workOfArt$
      .pipe(
        takeUntil(this.destroy$),
        filter((v) => v !== null)
      )
      .subscribe((workOfArt) => {
        this.data = workOfArt;
        this._cdr.markForCheck();
      });

    // Aquí puedes inicializar cualquier dato necesario cuando se abra el modal
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngAfterViewInit() {
    setTimeout(() => {
      this.initializeSwiper();
      this._cdr.markForCheck();
    }, 0);
  }

  initializeSwiper() {
    const swiper = new Swiper('.swiper-random-workart-modal', {
      slidesPerView: 'auto',
      loop: true, // Habilita el bucle
      spaceBetween: 20, // px entre slides
      autoplay: {
        delay: 3000,
      },
      // … cualquier otra opción que necesites …
    });
  }

  onThumbnailLoad(id: string) {
    this.loadedImages.add(id);
    this._cdr.markForCheck();
  }

  openImageWithSpace(event: Event): void {
    event.preventDefault();
    (event.currentTarget as HTMLAnchorElement).click();
  }

  // Método para cerrar el modal
  closeModal(): void {
    this.dialogRef.close();
  }

  openWorkOfArtModal(workOfArt: any): void {
    this.loading = true;
    this.dialogRef.close(workOfArt); // Cierra el modal actual si es necesario
  }
  onArtistClick(artist: any) {
    this.dialogRef.close(); // Cierra el modal actual
    this.router.navigate(['artist', artist._id]);
  }

  postFollowing(artist: any) {
    this._artistService.postfollowing(artist).subscribe(() => {
      // Actualizar el estado del artista en la lista

      this._cdr.markForCheck();
    });
  }

  postUnfollowing(artist: any) {
    this._artistService.postunfollowing(artist).subscribe(() => {
      // Actualizar el estado del artista en la lista
      this._cdr.markForCheck();
    });
  }

  postFavoriting(workOfArt: any) {
    this._workOfArtService.addFavoriteWorkOfArt(workOfArt._id).subscribe(() => {
      // Actualizar el estado de la obra de arte en la lista
      this._cdr.markForCheck();
    });
  }

  deleteFavoriting(workOfArt: any) {
    this._workOfArtService
      .removeFavoriteWorkOfArt(workOfArt._id)
      .subscribe(() => {
        // Actualizar el estado de la obra de arte en la lista
        this._cdr.markForCheck();
      });
  }

  // Build the actionable URL for a contact menu item, mirroring the artist
  // profile page so the modal's "Contactar" menu actually works (email ->
  // mailto, whatsapp -> wa.me, anything else -> URL).
  getContactUrl(item: any): string {
    if (!item || !item.value) {
      return '#';
    }

    switch (item.name?.toLowerCase()) {
      case 'email':
        return `mailto:${item.value}`;
      case 'whatsapp':
        // Remove any spaces, dashes, or plus signs from the phone number
        const cleanNumber = item.value.replace(/[\s\-\+]/g, '');
        return `https://wa.me/${cleanNumber}`;
      default:
        // Un valor sin esquema se resolvería como ruta relativa del sitio
        // (ABC-440); la normalización vive en un solo lugar.
        return urlExterna(item.value);
    }
  }
}
